import { useMemo, useState, type FormEvent } from "react";
import { CheckCircle2, AlertTriangle } from "lucide-react";

import type {
  BuildingService,
  ContextSetpoint,
  DataPointSample,
  DataPointSource,
} from "@/lib/api/building";
import type { ZoneEquipmentSignal, ZoneKpis } from "@/lib/api/agent";
import {
  BUILDING_SOURCE_KIND_LABEL,
  DATA_POINT_DOMAIN_COLOR,
  DATA_POINT_DOMAIN_LABEL,
  DATA_POINT_NATURE_COLOR,
  DOMAIN_ORDER,
  NATURE_ORDER,
  type DataPointDomain,
  type DataPointNature,
} from "@/lib/building-domain";
import { cardTonePill, inputGlowInner, inputGlowWrap } from "@/lib/card-styles";
import { cn } from "@/lib/utils";
import { componentLabel, useKbComponentLabels } from "@/lib/component-labels";
import {
  contextSetpointChipLabel,
  contextSetpointChipTone,
  contextSetpointControlEligibility,
  contextSetpointLabels,
} from "@/lib/context-setpoint-signal";
import { dataPointLabel } from "@/lib/data-point-labels";
import { ZoneKpiSourceBadge } from "@/components/ui/zone-kpi-badge";
import { zoneKpiSourceChip } from "@/lib/zone-kpi";
import { DataPointHistoryPanel } from "./DataPointHistoryPanel";
import { signal_group_key } from "./signal-group-key";
import { signal_group_chrome } from "./signal-group-chrome";
import { SignalSourceTree, type SignalTreeGroup } from "./SignalSourceTree";
import { signalNatureChip, zoneRowIsSubstituted } from "./signal-nature-chip";
import {
  is_row_visible,
  type VisibleSignalFilter,
} from "./visible-signal-filter";
import {
  projectSignaalitRows,
  countAktivoimattaSuggestions,
  countEiKytkettySuggestions,
  type SignaalitRow,
} from "./signaalit-rows";

type SortMode = "alpha" | "unit" | "component";

const SORT_MODES: SortMode[] = ["alpha", "unit", "component"];

const KIND_ORDER: Record<string, number> = {
  status: 0,
  sensor: 1,
  actuator: 2,
  setpoint: 3,
  calculated: 4,
};

const SORT_LABEL: Record<SortMode, string> = {
  alpha: "A–Ö",
  unit: "Yksikkö",
  component: "Komponentti",
};

function format_time(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff_ms = Date.now() - d.getTime();
  const diff_min = Math.floor(diff_ms / 60_000);
  if (diff_min < 1) return "juuri nyt";
  if (diff_min < 60) return `${diff_min} min sitten`;
  const diff_h = Math.floor(diff_min / 60);
  if (diff_h < 24) return `${diff_h} h sitten`;
  return d.toLocaleDateString("fi-FI");
}

interface SelectedSignal {
  source_id: string;
  source_name: string;
  name: string;
  label: string;
  unit: string | null;
  nature: string;
}

interface SourceGroup {
  group_key: string;
  source_id: string;
  source_name: string;
  source_type: "zone" | "equipment" | "building";
  primary_domain: DataPointDomain;
  visible_rows: SignaalitRow[];
  latest_recorded_at: string | undefined;
}

// Per-source KB coverage — only meaningful in "config" mode, sourced from the KB behaviors
// summary (agent domain). `activeCount` still annotates the header; the aktivoimatta badge
// counts undeclared observed rows (see countAktivoimattaSuggestions), not unwiredCount.
export interface SignalSourceCoverage {
  hasKb: boolean;
  activeCount: number;
  /** @deprecated Prefer countAktivoimattaSuggestions for the config badge. Kept for API shape. */
  unwiredCount: number;
}

interface Props {
  buildingId: string;
  sources: DataPointSource[];
  samples: DataPointSample[];
  behavior_labels?: Record<string, string>;
  // "live" (default): Datan laatu building-scope workspace — click a signal to see its value history.
  // "config": /configuration — header tags for kytkentä / aktivointi gaps; row chips
  // stay for syöte and asetusarvo only (not per-row kytketty / aktivoimatta).
  mode?: "live" | "config";
  coverage_by_source?: Record<string, SignalSourceCoverage>;
  /** Equipment source_id → declared_data_points (config mode header tags). */
  declared_by_source?: Record<string, string[]>;
  /** Equipment-only Signaalien kytkennät — map lähdesignaali → signaali. */
  onKytkennat?: (equipmentId: string) => void;
  /** Equipment-only Signaalien aktivointi — declare / undeclare KB ids. */
  onAktivointi?: (equipmentId: string) => void;
  /**
   * source_id → canonical data_point id → raw Lähdesignaali (CSV column).
   * Config mode only — from Building csv_signal_schemas.
   */
  source_signal_by_source?: Record<string, Record<string, string>>;
  /**
   * source_id → Lähdesignaali names still present on the connector CSV but
   * missing from the taught map (Device `unmapped_signals`). Config-mode
   * “ei kytketty” header tag — never infer from observed-without-source.
   */
  unmapped_lahdesignaalit_by_source?: Record<string, readonly string[]>;
  /**
   * Building's external-feed activation state (listBuildingServices). Gates the Ulkoiset
   * palvelut group per-signal: `is_active` means "in use for live optimization/display", not
   * whether the collector writes it (bug #1506) — an inactive service's rows stay hidden here
   * even though data_point_history keeps receiving them. Defaults to "none active" so the
   * group stays hidden until the caller has actually loaded services.
   */
  building_services?: BuildingService[];
  /**
   * Equipment source_id → its context-setpoint (asetusarvo) families (agent
   * `listEquipmentContextSetpoints`) — merged into that equipment's rows so each family
   * renders as exactly one Signaalit row, mapped or not (story #1515).
   */
  context_setpoints_by_source?: Record<string, ContextSetpoint[]>;
  /**
   * Operator-gated context-setpoint write controls — Konfiguraatio only (undefined on Datan
   * laatu / a non-operator, mirroring `onKytkennat`'s presence-gates-the-control pattern).
   * The caller re-reads the row's effective value/source after either write (never optimistic).
   */
  onAcceptContextSetpoint?: (
    equipmentId: string,
    contextSetpointId: string,
  ) => void | Promise<void>;
  onSetContextSetpointValue?: (
    equipmentId: string,
    contextSetpointId: string,
    value: number,
    unit: string | null,
  ) => void | Promise<void>;
  /**
   * Render-time signal visibility filter. Narrows which source/signal rows are rendered
   * while `sources` stays full (connection-source history resolution scans the whole set).
   * Absent → today's behaviour exactly. Present → only rows whose source_id appears render;
   * an entry without `names` shows all of that source's rows; with `names`, only those names.
   * Groups with no visible rows after filtering are suppressed. Mapping does not decide
   * membership: an embedded caller must include activated names (declared_data_points and
   * each context-setpoint family's id) so an unmapped asetusarvo synthetic row is not
   * dropped (see `build_equipment_visible_signals`).
   */
  visible_signals?: VisibleSignalFilter;
  /**
   * Nested in a parent panel that already names the selection (equipment/zone
   * detail). Drops accordion chrome: one group renders as a flat row list; several
   * groups keep a static always-open header so sources stay distinguishable.
   */
  embedded?: boolean;
  /** Zone id → equipment-publishes. Compare rows render under the zone as Syöte, same chrome as AHU ← chiller. */
  zone_equipment_signals?: Record<string, ZoneEquipmentSignal[]>;
  /** Zone id → the agent's resolved KPI triple (getZoneKpisSummary, task #1761) — the same
   *  step/raw value/provenance/confidence the Sisäilmaolosuhteet card and Energiatehokkuus show
   *  for occupancy_count/co2/temperature (task #1766: @/lib/zone-kpi.ts). */
  zone_kpis?: Record<string, ZoneKpis>;
}

function primary_domain_of(rows: SignaalitRow[]): DataPointDomain {
  const counts = new Map<DataPointDomain, number>();
  for (const r of rows) counts.set(r.domain, (counts.get(r.domain) ?? 0) + 1);
  let best: DataPointDomain = rows[0].domain;
  let best_n = 0;
  for (const [d, n] of counts) {
    if (n > best_n) {
      best = d;
      best_n = n;
    }
  }
  return best;
}

function sort_rows(
  rows: SignaalitRow[],
  mode: SortMode,
  behavior_labels: Record<string, string> | undefined,
): SignaalitRow[] {
  const label = (r: SignaalitRow) => dataPointLabel(r.name, behavior_labels);
  switch (mode) {
    case "alpha":
      return [...rows].sort((a, b) => label(a).localeCompare(label(b), "fi"));
    case "unit":
      return [...rows].sort(
        (a, b) =>
          a.unit.localeCompare(b.unit, "fi") ||
          label(a).localeCompare(label(b), "fi"),
      );
    case "component":
      return [...rows].sort(
        (a, b) =>
          (a.component_id ?? "").localeCompare(b.component_id ?? "", "fi") ||
          (KIND_ORDER[a.kind ?? ""] ?? 9) - (KIND_ORDER[b.kind ?? ""] ?? 9) ||
          label(a).localeCompare(label(b), "fi"),
      );
  }
}

export function SignaalitList({
  buildingId,
  sources,
  samples,
  behavior_labels,
  mode = "live",
  coverage_by_source,
  declared_by_source,
  onKytkennat,
  onAktivointi,
  source_signal_by_source,
  unmapped_lahdesignaalit_by_source,
  building_services,
  context_setpoints_by_source,
  onAcceptContextSetpoint,
  onSetContextSetpointValue,
  visible_signals,
  embedded = false,
  zone_equipment_signals = {},
  zone_kpis = {},
}: Props) {
  const component_labels = useKbComponentLabels(
    sources.map((s) => s.equipment_type),
  );
  // Fallback labels for a context-setpoint row that merges into no existing signal (unmapped
  // families have no other row to already carry a Finnish name) — real labels (behavior_labels)
  // always win, this only fills the gap.
  const labels_with_context_setpoints = useMemo(() => {
    const all_context_setpoints = Object.values(
      context_setpoints_by_source ?? {},
    ).flat();
    if (all_context_setpoints.length === 0) return behavior_labels;
    return {
      ...contextSetpointLabels(all_context_setpoints),
      ...behavior_labels,
    };
  }, [context_setpoints_by_source, behavior_labels]);
  const [active_natures, set_active_natures] = useState<Set<DataPointNature>>(
    new Set(NATURE_ORDER),
  );
  const [search, set_search] = useState("");
  const [sort_mode, set_sort_mode] = useState<SortMode>("component");
  const [selected_signal, set_selected_signal] =
    useState<SelectedSignal | null>(null);

  // Membership + row fields live in projectSignaalitRows; this list only filters/sorts/groups.
  const all_rows = useMemo(
    () =>
      projectSignaalitRows({
        sources,
        samples,
        mode,
        building_services,
        context_setpoints_by_source,
        zone_equipment_signals,
        zone_kpis,
      }),
    [
      sources,
      samples,
      mode,
      building_services,
      context_setpoints_by_source,
      zone_equipment_signals,
      zone_kpis,
    ],
  );

  // Apply search filter to rows — labels_with_context_setpoints is stable after mount so
  // omitting from deps is safe
  const search_rows = useMemo(() => {
    const term = search.toLowerCase();
    if (!term) return all_rows;
    return all_rows.filter(
      (r) =>
        dataPointLabel(r.name, labels_with_context_setpoints)
          .toLowerCase()
          .includes(term) || r.source_name.toLowerCase().includes(term),
    );
  }, [all_rows, search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build source groups: domain + nature filters and sort mode apply inside each group
  const groups = useMemo<SourceGroup[]>(() => {
    const map = new Map<
      string,
      {
        meta: Omit<SourceGroup, "visible_rows" | "primary_domain">;
        visible: SignaalitRow[];
      }
    >();

    for (const row of search_rows) {
      const group_key = signal_group_key(
        row.source_type,
        row.source_id,
        row.domain,
      );
      if (!map.has(group_key)) {
        map.set(group_key, {
          meta: {
            group_key,
            source_id: row.source_id,
            source_name: row.source_name,
            source_type: row.source_type,
            latest_recorded_at: undefined,
          },
          visible: [],
        });
      }
      const entry = map.get(group_key)!;
      if (
        active_natures.has(
          signalNatureChip({
            nature: row.nature,
            kind: row.kind,
            connection_source: row.connection_source,
            substituted: zoneRowIsSubstituted(
              row.source_id,
              row.name,
              zone_equipment_signals,
            ),
          }).color_nature,
        ) &&
        is_row_visible(row.source_id, row.name, visible_signals)
      )
        entry.visible.push(row);
      if (
        row.recorded_at &&
        (!entry.meta.latest_recorded_at ||
          row.recorded_at > entry.meta.latest_recorded_at)
      ) {
        entry.meta.latest_recorded_at = row.recorded_at;
      }
    }

    const result: SourceGroup[] = [];
    for (const { meta, visible } of map.values()) {
      if (visible.length === 0) continue;
      result.push({
        ...meta,
        primary_domain: primary_domain_of(visible),
        visible_rows: sort_rows(
          visible,
          sort_mode,
          labels_with_context_setpoints,
        ),
      });
    }

    // Sort: by DOMAIN_ORDER of primary domain, then equipment before zone before building, then name
    const type_rank: Record<string, number> = {
      equipment: 0,
      building: 1,
      zone: 2,
    };
    result.sort((a, b) => {
      const di =
        DOMAIN_ORDER.indexOf(a.primary_domain) -
        DOMAIN_ORDER.indexOf(b.primary_domain);
      if (di !== 0) return di;
      const ti =
        (type_rank[a.source_type] ?? 9) - (type_rank[b.source_type] ?? 9);
      if (ti !== 0) return ti;
      return a.source_name.localeCompare(b.source_name, "fi");
    });

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    search_rows,
    active_natures,
    sort_mode,
    visible_signals,
    zone_equipment_signals,
  ]);

  const chrome = signal_group_chrome(embedded, groups.length);

  function toggle_nature(nature: DataPointNature) {
    set_active_natures((prev) => {
      const next = new Set(prev);
      if (next.has(nature)) next.delete(nature);
      else next.add(nature);
      return next;
    });
  }

  // Map each source group to the presentational tree's shape: a header node (badges +
  // counts + freshness [+ KB coverage in config mode]) and the already-built signal row
  // nodes. Row content, sub-headers, and the history-panel click target stay here — the
  // tree owns chrome (accordion / static / flat) via `groupChrome`.
  //
  // Memoized so row nodes aren't rebuilt on unrelated state (history panel). Expansion
  // itself is owned by SignalSourceTree via next_expanded_group_keys — not this identity.
  const tree_groups: SignalTreeGroup[] = useMemo(() => {
    return groups.map((group) => {
      const color = DATA_POINT_DOMAIN_COLOR[group.primary_domain];
      const is_building_source = group.source_type === "building";
      const coverage = coverage_by_source?.[group.source_id];
      const declared = new Set(declared_by_source?.[group.source_id] ?? []);
      const source_signals = source_signal_by_source?.[group.source_id] ?? {};
      const aktivoimattaCount =
        mode === "config" && group.source_type === "equipment"
          ? countAktivoimattaSuggestions(
              group.visible_rows,
              declared,
              source_signals,
            )
          : 0;
      const eiKytkettyCount =
        mode === "config" && group.source_type === "equipment"
          ? // Unmapped lähdesignaalit only — never “observed without source” (false error).
            countEiKytkettySuggestions(
              unmapped_lahdesignaalit_by_source?.[group.source_id] ?? [],
            )
          : 0;
      const header = (
        <>
          <div className="flex flex-1 items-baseline gap-2 min-w-0">
            <span className="text-sm font-medium truncate">
              {is_building_source
                ? BUILDING_SOURCE_KIND_LABEL[group.primary_domain]
                : group.source_name}
            </span>
            {is_building_source && (
              <span className="text-xs text-muted-foreground truncate">
                {group.source_name}
              </span>
            )}
          </div>

          {group.source_type === "zone" ? (
            <span className="shrink-0 rounded px-1.5 py-0.5 text-xs bg-muted text-muted-foreground">
              Vyöhyke
            </span>
          ) : (
            // Building-level groups already name themselves via BUILDING_SOURCE_KIND_LABEL
            // above — no separate domain badge, since domain stays internal classification
            // for external feeds (story #882), not a second label next to the title.
            !is_building_source && (
              <span
                className="shrink-0 rounded px-1.5 py-0.5 text-xs"
                style={{ backgroundColor: color + "22", color }}
              >
                {DATA_POINT_DOMAIN_LABEL[group.primary_domain]}
              </span>
            )
          )}

          {/* A live row's chip sits a constant 232px (gap-3 + value w-28 + gap-3 +
              time w-24) before the row's right edge, regardless of name/chip length —
              value and time are fixed-width. This badge needs the same constant
              232px of *fixed*-width content after it to land at the same X. The
              count text below is fixed at the same w-28 as `value` for exactly
              that reason — but only when it's short (no coverage suffix): the
              coverage-fitted count (config mode) can run much longer, so it keeps
              its natural width there rather than truncating real information. */}
          <span
            className={cn(
              "shrink-0 text-right text-xs text-muted-foreground",
              !coverage?.hasKb && "w-28 truncate",
            )}
          >
            {group.visible_rows.length} signaalia
            {coverage?.hasKb &&
              ` · ${coverage.activeCount} aktiivista kyvykkyyttä`}
          </span>

          {mode === "config" ? (
            group.source_type === "equipment" ? (
              <div className="flex shrink-0 flex-col items-end gap-1">
                {onKytkennat && (
                  <div className="flex items-center gap-1.5">
                    <ActionGapTag
                      hasGap={eiKytkettyCount > 0}
                      okLabel="kaikki kytketty"
                      gapLabel={`ei kytketty ${eiKytkettyCount}`}
                      okTitle="Ei kytkemättömiä lähdesignaaleja"
                      gapTitle="Lähdesignaaleja ilman signaaliparia — avaa Signaalien kytkennät"
                    />
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        onKytkennat(group.source_id);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          e.stopPropagation();
                          onKytkennat(group.source_id);
                        }
                      }}
                      className="inline-flex w-[9.5rem] justify-center rounded-md border border-border bg-surface px-2 py-0.5 text-[10px] font-medium text-foreground hover:border-primary/40"
                    >
                      Signaalien kytkennät
                    </span>
                  </div>
                )}
                {onAktivointi && (
                  <div className="flex items-center gap-1.5">
                    <ActionGapTag
                      hasGap={aktivoimattaCount > 0}
                      okLabel="kaikki käytössä"
                      gapLabel={`aktivoimatta ${aktivoimattaCount}`}
                      okTitle="Kaikki havaitut signaalit on aktivoitu"
                      gapTitle="Havaittuja signaaleja ei ole vielä aktivoitu — avaa Signaalien aktivointi"
                    />
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        onAktivointi(group.source_id);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          e.stopPropagation();
                          onAktivointi(group.source_id);
                        }
                      }}
                      className="inline-flex w-[9.5rem] justify-center rounded-md border border-border bg-surface px-2 py-0.5 text-[10px] font-medium text-foreground hover:border-primary/40"
                    >
                      Signaalien aktivointi
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <span className="shrink-0 rounded-full border border-border px-2 py-0.5 text-[10px] text-muted-foreground">
                vain katselu
              </span>
            )
          ) : (
            <span className="shrink-0 w-24 text-right text-xs text-muted-foreground/50">
              {format_time(group.latest_recorded_at)}
            </span>
          )}
        </>
      );

      const rows =
        mode === "config"
          ? config_rows(
              group,
              labels_with_context_setpoints,
              source_signals,
              component_labels,
              onAcceptContextSetpoint,
              onSetContextSetpointValue,
            )
          : live_rows(
              group,
              sort_mode,
              color,
              sources,
              labels_with_context_setpoints,
              component_labels,
              set_selected_signal,
              chrome === "accordion",
              zone_equipment_signals,
            );

      return { key: group.group_key, header, rows };
    });
  }, [
    groups,
    sort_mode,
    sources,
    labels_with_context_setpoints,
    component_labels,
    mode,
    coverage_by_source,
    declared_by_source,
    source_signal_by_source,
    unmapped_lahdesignaalit_by_source,
    onKytkennat,
    onAktivointi,
    onAcceptContextSetpoint,
    onSetContextSetpointValue,
    chrome,
    zone_equipment_signals,
  ]);

  return (
    <>
      <SignalSourceTree
        groups={tree_groups}
        groupChrome={chrome}
        activeNatures={active_natures}
        onToggleNature={toggle_nature}
        search={search}
        onSearchChange={set_search}
        emptyLabel={
          mode === "config" ? "Ei konfiguroituja signaaleja" : "Ei signaaleja"
        }
        searchPlaceholder={
          mode === "config" ? "Hae signaalia tai lähdettä..." : undefined
        }
        sort={{
          modes: SORT_MODES,
          labels: SORT_LABEL,
          value: sort_mode,
          onChange: set_sort_mode,
        }}
      />

      {mode === "live" && selected_signal && (
        <DataPointHistoryPanel
          buildingId={buildingId}
          sourceId={selected_signal.source_id}
          sourceName={selected_signal.source_name}
          signalName={selected_signal.name}
          label={selected_signal.label}
          unit={selected_signal.unit}
          nature={
            selected_signal.nature as import("@/lib/api/building").DataPointNature
          }
          onClose={() => set_selected_signal(null)}
        />
      )}
    </>
  );
}

// Group header text starts at px-4 (16px) + the accordion chevron's own box
// (SignalSourceTree's `h-3.5` svg, 14px) + the gap-3 between them (12px) = 42px.
// A live row's text must start there too when its group has that chevron —
// pl-10 (40px) was a 2px-off approximation of this, visibly misaligned.
const GROUP_HEADER_ALIGN_PADDING = "pl-[42px]";

// "live" mode rows — click to open the value-history panel; shows the latest value + freshness.
function live_rows(
  group: SourceGroup,
  sort_mode: SortMode,
  header_color: string,
  sources: DataPointSource[],
  behavior_labels: Record<string, string> | undefined,
  component_labels: Record<string, string>,
  set_selected_signal: (s: SelectedSignal) => void,
  chevron_indent: boolean,
  zone_equipment_signals: Record<string, ZoneEquipmentSignal[]>,
) {
  let last_component: string | null = null;
  let last_unit: string | null = null;
  return group.visible_rows.map((row, idx) => {
    const chip = signalNatureChip({
      nature: row.nature,
      kind: row.kind,
      connection_source: row.connection_source,
      substituted: zoneRowIsSubstituted(
        row.source_id,
        row.name,
        zone_equipment_signals,
      ),
    });
    const nature_color =
      DATA_POINT_NATURE_COLOR[chip.color_nature] ?? header_color;

    const show_component_header =
      sort_mode === "component" &&
      row.component_id !== null &&
      row.component_id !== last_component;
    if (show_component_header) {
      last_component = row.component_id;
    }

    const show_unit_header = sort_mode === "unit" && row.unit !== last_unit;
    if (show_unit_header) {
      last_unit = row.unit;
    }

    return (
      <div key={`${row.name}-${idx}`}>
        {show_component_header && (
          <div
            className={cn(
              "px-4 pb-0.5 pt-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60",
              chevron_indent && GROUP_HEADER_ALIGN_PADDING,
            )}
          >
            {componentLabel(row.component_id!, component_labels)}
          </div>
        )}
        {show_unit_header && (
          <div
            className={cn(
              "px-4 pb-0.5 pt-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60",
              chevron_indent && GROUP_HEADER_ALIGN_PADDING,
            )}
          >
            {row.unit || "—"}
          </div>
        )}
        <button
          className={cn(
            "flex w-full items-start gap-3 px-4 py-2 hover:bg-muted/30 text-left transition-colors",
            chevron_indent && GROUP_HEADER_ALIGN_PADDING,
          )}
          title={
            row.connection_source_name
              ? `Syöte ← ${row.connection_source_name}`
              : "Näytä historia"
          }
          onClick={() => {
            if (row.connection_source && row.connection_upstream_name) {
              const upstream = row.connection_source_id
                ? sources.find((s) => s.source_id === row.connection_source_id)
                : sources.find(
                    (s) => s.equipment_type === row.connection_source,
                  );
              if (upstream) {
                const upstream_dp = upstream.available_names.find(
                  (n) => n.name === row.connection_upstream_name,
                );
                set_selected_signal({
                  source_id: upstream.source_id,
                  source_name: upstream.source_name,
                  name: row.connection_upstream_name,
                  label: upstream_dp?.label_fi ?? row.connection_upstream_name,
                  unit: upstream_dp?.unit ?? (row.unit || null),
                  nature: upstream_dp?.nature ?? "sensor",
                });
                return;
              }
            }
            set_selected_signal({
              source_id: row.source_id,
              source_name: row.source_name,
              name: row.name,
              label: dataPointLabel(row.name, behavior_labels),
              unit: row.unit || null,
              nature: row.nature,
            });
          }}
        >
          <span className="flex-1 min-w-0">
            <span className="block text-sm text-muted-foreground">
              {dataPointLabel(row.name, behavior_labels)}
              {row.connection_source_name && (
                <span className="ml-1.5 text-[10px] text-muted-foreground/50">
                  ← {row.connection_source_name}
                </span>
              )}
            </span>
          </span>
          <span className="flex shrink-0 items-center gap-1 mt-0.5">
            {/* Two orthogonal facts, two chips: where the value comes from
                (laite/opittu/käyttäjä/ei asetettu) first, in a fixed-width slot — present
                but empty on a plain row — then what kind of signal this is (Asetusarvo).
                The fixed slot (not just source-before-kind ordering) is what actually
                lines the kind chip up in the same column on every row: without it, the
                kind chip's x-position would still drift with the source label's text
                width ("laite" vs "ei asetettu"). */}
            <span className="inline-flex w-20 shrink-0 justify-center">
              {row.context_setpoint && (
                <span
                  className={cn(
                    "rounded-full border px-1.5 py-0.5 text-xs",
                    cardTonePill[
                      contextSetpointChipTone(
                        row.context_setpoint.effective_source,
                      )
                    ],
                  )}
                >
                  {contextSetpointChipLabel(
                    row.context_setpoint.effective_source,
                  )}
                </span>
              )}
            </span>
            {row.zone_kpi?.ready &&
              (() => {
                const zone_kpi_chip = zoneKpiSourceChip(row.zone_kpi);
                return zone_kpi_chip ? (
                  <ZoneKpiSourceBadge chip={zone_kpi_chip} />
                ) : null;
              })()}
            <span
              className="shrink-0 rounded px-1.5 py-0.5 text-xs"
              style={{
                backgroundColor: nature_color + "22",
                color: nature_color,
              }}
            >
              {chip.label}
            </span>
          </span>
          <span className="font-mono text-sm w-28 text-right shrink-0 mt-0.5">
            {row.value}
          </span>
          <span className="shrink-0 w-24 text-right text-xs text-muted-foreground/50 mt-0.5">
            {format_time(row.recorded_at)}
          </span>
        </button>
      </div>
    );
  });
}

// "config" mode rows — mapping coverage only, no live value, not clickable.
// kytketty / aktivoimatta live on the group header tags, not per row.
function config_rows(
  group: SourceGroup,
  behavior_labels: Record<string, string> | undefined,
  source_signals: Record<string, string>,
  component_labels: Record<string, string>,
  on_accept_context_setpoint:
    | ((equipmentId: string, contextSetpointId: string) => void | Promise<void>)
    | undefined,
  on_set_context_setpoint_value:
    | ((
        equipmentId: string,
        contextSetpointId: string,
        value: number,
        unit: string | null,
      ) => void | Promise<void>)
    | undefined,
) {
  return [
    <div
      key="__cols"
      className="grid grid-cols-[1.2fr_1fr_0.8fr_1fr] items-center gap-2 px-4 py-1.5 pl-10 text-[10px] uppercase tracking-[0.14em] text-muted-foreground"
    >
      <div>Signaali</div>
      <div>Lähdesignaali</div>
      <div>Komponentti</div>
      <div className="text-right">Tila</div>
    </div>,
    ...group.visible_rows.map((row) => {
      const label = dataPointLabel(row.name, behavior_labels);
      const source_signal = source_signals[row.name];
      const context_setpoint = row.context_setpoint;
      return (
        <div
          key={row.name}
          className="grid grid-cols-[1.2fr_1fr_0.8fr_1fr] items-center gap-2 px-4 py-2 pl-10 text-[11px]"
        >
          <div className="truncate text-foreground/90" title={label}>
            {label}
          </div>
          <div
            className="truncate font-mono text-[10px] text-muted-foreground"
            title={source_signal}
          >
            {source_signal?.trim() ? source_signal : "—"}
          </div>
          <div className="truncate text-muted-foreground">
            {row.component_id
              ? componentLabel(row.component_id, component_labels)
              : "—"}
          </div>
          <div className="flex flex-wrap items-center justify-end gap-1">
            {context_setpoint ? (
              <>
                <span
                  className={cn(
                    "rounded-full border px-2 py-0.5 text-[10px]",
                    cardTonePill[
                      contextSetpointChipTone(context_setpoint.effective_source)
                    ],
                  )}
                  title="Asetusarvon lähde"
                >
                  {contextSetpointChipLabel(context_setpoint.effective_source)}
                </span>
                {on_accept_context_setpoint &&
                  on_set_context_setpoint_value && (
                    <ContextSetpointControls
                      eligibility={contextSetpointControlEligibility(
                        context_setpoint,
                      )}
                      unit={context_setpoint.unit}
                      on_accept={() =>
                        on_accept_context_setpoint(
                          row.source_id,
                          context_setpoint.context_setpoint_id,
                        )
                      }
                      on_set_value={(value) =>
                        on_set_context_setpoint_value(
                          row.source_id,
                          context_setpoint.context_setpoint_id,
                          value,
                          context_setpoint.unit || null,
                        )
                      }
                    />
                  )}
              </>
            ) : row.connection_source ? (
              <span className="rounded-full border border-primary/40 bg-primary/5 px-2 py-0.5 text-primary">
                syöte
              </span>
            ) : null}
          </div>
        </div>
      );
    }),
  ];
}

// Inline accept / typed-fill controls for an unmapped context-setpoint (asetusarvo) row —
// Konfiguraatio only (Datan laatu never renders this: the caller gates the props that
// reach here). Renders nothing when eligibility offers neither control (the mapped case).
function ContextSetpointControls({
  eligibility,
  unit,
  on_accept,
  on_set_value,
}: {
  eligibility: { can_accept: boolean; can_type_value: boolean };
  unit: string;
  on_accept: () => void | Promise<void>;
  on_set_value: (value: number) => void | Promise<void>;
}) {
  const [typed, set_typed] = useState("");
  const [busy, set_busy] = useState(false);

  if (!eligibility.can_accept && !eligibility.can_type_value) return null;

  async function accept() {
    set_busy(true);
    try {
      await on_accept();
    } finally {
      set_busy(false);
    }
  }

  async function submit_typed(e: FormEvent) {
    e.preventDefault();
    const parsed = Number(typed.replace(",", "."));
    if (!Number.isFinite(parsed)) return;
    set_busy(true);
    try {
      await on_set_value(parsed);
      set_typed("");
    } finally {
      set_busy(false);
    }
  }

  return (
    <div className="flex items-center gap-1">
      {eligibility.can_accept && (
        <button
          type="button"
          disabled={busy}
          onClick={accept}
          className="rounded-md border border-border bg-surface px-1.5 py-0.5 text-[10px] font-medium text-foreground hover:border-primary/40 disabled:opacity-50"
        >
          Hyväksy
        </button>
      )}
      {eligibility.can_type_value && (
        <form onSubmit={submit_typed} className="flex items-center gap-1">
          <div className={inputGlowWrap}>
            <div className={cn(inputGlowInner, "px-1.5 py-0.5")}>
              <input
                value={typed}
                onChange={(e) => set_typed(e.target.value)}
                placeholder={unit || "arvo"}
                disabled={busy}
                className="w-14 bg-transparent text-[10px] outline-none"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={busy || typed.trim() === ""}
            className="rounded-md border border-border bg-surface px-1.5 py-0.5 text-[10px] font-medium text-foreground hover:border-primary/40 disabled:opacity-50"
          >
            Aseta
          </button>
        </form>
      )}
    </div>
  );
}

function ActionGapTag({
  hasGap,
  okLabel,
  gapLabel,
  okTitle,
  gapTitle,
}: {
  hasGap: boolean;
  okLabel: string;
  gapLabel: string;
  okTitle: string;
  gapTitle: string;
}) {
  if (!hasGap) {
    return (
      <span
        className={cn(
          "shrink-0 rounded-full border px-2 py-0.5 text-[10px]",
          cardTonePill.ok,
        )}
        title={okTitle}
      >
        <CheckCircle2 className="h-3 w-3 inline mr-1" />
        {okLabel}
      </span>
    );
  }
  return (
    <span
      className={cn(
        "shrink-0 rounded-full border px-2 py-0.5 text-[10px]",
        cardTonePill.warn,
      )}
      title={gapTitle}
    >
      <AlertTriangle className="h-3 w-3 inline mr-1" />
      {gapLabel}
    </span>
  );
}
